import React, { useState, useRef, useEffect } from "react";
import { Grid, Typography } from "@material-ui/core";
import { StyledInputLabel, StyledUploadInput } from "../StyledComponents";
import ChevronLeftIcon from "@material-ui/icons/ChevronLeft";
import ChevronRightIcon from "@material-ui/icons/ChevronRight";
import ImageCarouselModal from "./ImageCarouselModal";

interface WizardStep1PhotosProps {
  photoRef: React.RefObject<HTMLInputElement>;
  certificateRef: React.RefObject<HTMLInputElement>;
  handleFileChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  previewUrls: any[];
  handleRemovePhoto: (index: number) => void;
  isExtracting: boolean;
  extractError?: string | null;
}

const WizardStep1Photos: React.FC<WizardStep1PhotosProps> = ({
  photoRef,
  certificateRef,
  handleFileChange,
  previewUrls,
  handleRemovePhoto,
  isExtracting,
  extractError,
}) => {
  const [carouselOpen, setCarouselOpen] = useState(false);
  const [carouselIndex, setCarouselIndex] = useState(0);
  const [showArrows, setShowArrows] = useState(false);
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  const checkOverflow = () => {
    if (scrollContainerRef.current) {
      const { scrollWidth, clientWidth } = scrollContainerRef.current;
      setShowArrows(scrollWidth > clientWidth);
    }
  };

  useEffect(() => {
    checkOverflow();
    window.addEventListener("resize", checkOverflow);
    return () => window.removeEventListener("resize", checkOverflow);
  }, [previewUrls]);

  useEffect(() => {
    const timer = setTimeout(checkOverflow, 100);
    return () => clearTimeout(timer);
  });

  const handleImageClick = (index: number) => {
    setCarouselIndex(index);
    setCarouselOpen(true);
  };

  const scroll = (offset: number) => {
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollBy({ left: offset, behavior: "smooth" });
    }
  };

  return (
    <Grid container spacing={4}>
      <Grid item xs={12}>
        <Typography variant="h5" style={{ fontWeight: 'bold', marginBottom: '0.5rem' }}>Upload photos</Typography>
        <Typography style={{ color: '#666', marginBottom: '2rem' }}>Up to 10 clear, well-lit shots. Hallmark and certificate close-ups sharpen the price.</Typography>
      </Grid>

      <Grid item xs={12}>
        <StyledUploadInput onClick={() => !isExtracting && photoRef.current?.click()} style={{ background: '#fff', border: '2px dashed #EAEAEA', borderRadius: '8px', padding: '1.25rem 1rem', textAlign: 'center', cursor: isExtracting ? 'not-allowed' : 'pointer', opacity: isExtracting ? 0.6 : 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', width: '100%', boxSizing: 'border-box', whiteSpace: 'normal' }}>
          <span style={{ fontSize: '2.5rem', color: '#d92b3c', fontWeight: 300, lineHeight: 1 }}>+</span>
          <StyledInputLabel style={{ display: 'block', margin: '0.5rem 0 0.25rem 0', fontWeight: 'bold', fontSize: '1.1rem', color: '#1A1A1A' }}>
            Drop photos or tap to upload
          </StyledInputLabel>
          <Typography style={{ color: '#666', fontSize: '0.9rem' }}>PNG, JPG, JPEG, JFIF, HEIC, WebP · max 25MB total · front, hallmark, stones, certificate</Typography>
          <input accept="image/png,image/jpeg,image/webp,image/heic,image/heif,image/jfif,.png,.jpg,.jpeg,.jfif,.webp,.heic,.heif" ref={photoRef} hidden id="photo" name="photo" type="file" multiple onChange={handleFileChange} />
        </StyledUploadInput>
        {extractError && (
          <Typography style={{ color: '#d92b3c', fontSize: '0.9rem', marginTop: '1rem', textAlign: 'center', fontWeight: 'bold' }}>
            {extractError}
          </Typography>
        )}
      </Grid>

      {previewUrls.length > 0 && (
        <Grid item xs={12}>
          <div style={{ border: '1px solid #EAEAEA', borderRadius: '8px', padding: '1.5rem', background: '#FAFAFA' }}>
            <h4 style={{ margin: '0 0 1rem 0', color: '#1A1A1A', fontSize: '0.9rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <svg style={{ color: '#d92b3c' }} xmlns="http://www.w3.org/2000/svg" height="16" width="16" fill="currentColor" viewBox="0 0 24 24"><path d="M21 19V5c0-1.1-.9-2-2-2H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2zM8.5 13.5l2.5 3.01L14.5 12l4.5 6H5l3.5-4.5z" /></svg>
              Your Uploads ({previewUrls.length})
            </h4>
            <div style={{ display: "flex", alignItems: "center", width: "100%", gap: "12px" }}>
              {showArrows && (
                <button
                  onClick={() => scroll(-200)}
                  style={{
                    zIndex: 2,
                    backgroundColor: "#fff",
                    boxShadow: "0 2px 8px rgba(0,0,0,0.2)",
                    border: "none",
                    borderRadius: "50%",
                    color: "#d92b3c",
                    cursor: "pointer",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    width: "28px",
                    height: "28px",
                    padding: 0,
                    flexShrink: 0
                  }}
                >
                  <ChevronLeftIcon fontSize="small" />
                </button>
              )}

              <div
                className="hide-scroll"
                ref={scrollContainerRef}
                style={{
                  display: "flex",
                  overflowX: "auto",
                  gap: "1rem",
                  scrollbarWidth: "none",
                  msOverflowStyle: "none",
                  padding: "0.5rem",
                  margin: "-0.5rem",
                  flex: 1
                }}
              >
                <style>{`
                  .hide-scroll::-webkit-scrollbar {
                    display: none;
                  }
                `}</style>
                {previewUrls.map((item, index) => {
                  const displayUrl = item.tempThumbFileUrl || item.url;
                  return (
                    <div
                      key={index}
                      onClick={() => handleImageClick(index)}
                      style={{
                        position: 'relative',
                        background: '#fff',
                        borderRadius: '8px',
                        aspectRatio: '3/4',
                        width: '120px',
                        flexShrink: 0,
                        border: '2px solid #fff',
                        boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        overflow: 'hidden',
                        cursor: 'pointer'
                      }}>
                      {(typeof displayUrl === 'string' && (displayUrl.startsWith('http') || displayUrl.startsWith('data:image/') || displayUrl.startsWith('blob:'))) ? (
                        <>
                          <img src={displayUrl} alt="" aria-hidden="true" style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', objectFit: 'cover', filter: 'blur(12px) brightness(0.7)', transform: 'scale(1.1)' }} />
                          <img src={displayUrl} alt={item.file.name} style={{ width: '100%', height: '100%', objectFit: 'contain', position: 'relative' }} />
                        </>
                      ) : (
                        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100%', background: '#fff', padding: '1rem' }}>
                          <svg xmlns="http://www.w3.org/2000/svg" height="40" width="40" fill="#d92b3c" viewBox="0 0 24 24"><path d="M21 19V5c0-1.1-.9-2-2-2H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2zM8.5 13.5l2.5 3.01L14.5 12l4.5 6H5l3.5-4.5z" /></svg>
                        </div>
                      )}
                      <div style={{
                        position: 'absolute',
                        bottom: 0,
                        left: 0,
                        right: 0,
                        background: 'rgba(0,0,0,0.6)',
                        padding: '4px 8px',
                        backdropFilter: 'blur(4px)'
                      }}>
                        <span style={{
                          fontSize: '0.75rem',
                          color: '#fff',
                          display: 'block',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                          textAlign: 'center'
                        }}>
                          {item.file.name}
                        </span>
                      </div>
                      <button
                        type="button"
                        disabled={isExtracting}
                        onClick={(e) => { e.stopPropagation(); e.preventDefault(); handleRemovePhoto(index); }}
                        style={{
                          position: 'absolute',
                          top: '8px',
                          right: '8px',
                          background: 'rgba(0,0,0,0.6)',
                          color: 'white',
                          border: 'none',
                          borderRadius: '50%',
                          width: '26px',
                          height: '26px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          cursor: isExtracting ? 'not-allowed' : 'pointer',
                          padding: 0,
                          opacity: isExtracting ? 0.5 : 1
                        }}
                      >
                        <svg xmlns="http://www.w3.org/2000/svg" height="14" width="14" fill="currentColor" viewBox="0 0 24 24"><path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z" /></svg>
                      </button>
                    </div>
                  );
                })}
              </div>

              {showArrows && (
                <button
                  onClick={() => scroll(200)}
                  style={{
                    zIndex: 2,
                    backgroundColor: "#fff",
                    boxShadow: "0 2px 8px rgba(0,0,0,0.2)",
                    border: "none",
                    borderRadius: "50%",
                    color: "#d92b3c",
                    cursor: "pointer",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    width: "28px",
                    height: "28px",
                    padding: 0,
                    flexShrink: 0
                  }}
                >
                  <ChevronRightIcon fontSize="small" />
                </button>
              )}
            </div>
          </div>
        </Grid>
      )}

      <Grid item xs={12}>
        <Grid container spacing={2}>
          {[
            { title: 'Front view', desc: 'Stone centred, sharp focus', icon: <svg xmlns="http://www.w3.org/2000/svg" height="20" width="20" fill="currentColor" viewBox="0 0 24 24"><path d="M12 2L2 12l10 10 10-10L12 2z" /></svg> },
            { title: 'Certificate', desc: 'GIA / IGI / CGL / AGT card', icon: <svg xmlns="http://www.w3.org/2000/svg" height="20" width="20" fill="currentColor" viewBox="0 0 24 24"><path d="M14 2H6c-1.1 0-1.99.9-1.99 2L4 20c0 1.1.89 2 1.99 2H18c1.1 0 2-.9 2-2V8l-6-6zm2 16H8v-2h8v2zm0-4H8v-2h8v2zm-3-5V3.5L18.5 9H13z" /></svg> },
            { title: 'Hallmark', desc: 'e.g. Pt900, 2.06', icon: <svg xmlns="http://www.w3.org/2000/svg" height="20" width="20" fill="currentColor" viewBox="0 0 24 24"><path d="M21.41 11.58l-9-9C12.05 2.22 11.55 2 11 2H4c-1.1 0-2 .9-2 2v7c0 .55.22 1.05.59 1.41l9 9c.36.36.86.58 1.41.58.55 0 1.05-.22 1.41-.59l7-7c.37-.36.59-.86.59-1.41 0-.55-.23-1.06-.59-1.41zM5.5 7C4.67 7 4 6.33 4 5.5S4.67 4 5.5 4 7 4.67 7 5.5 6.33 7 5.5 7z" /></svg> },
            { title: 'Stones detail', desc: 'Macro of gems & prongs', icon: <svg xmlns="http://www.w3.org/2000/svg" height="20" width="20" fill="currentColor" viewBox="0 0 24 24"><path d="M12 2l2.4 7.6L22 12l-7.6 2.4L12 22l-2.4-7.6L2 12l7.6-2.4z" /></svg> }
          ].map((req, i) => (
            <Grid item xs={6} sm={6} md={3} key={i}>
              <div style={{
                background: '#fff',
                borderRadius: '8px',
                padding: '1.25rem',
                border: '1px solid #EAEAEA',
                display: 'flex',
                flexDirection: 'column',
                height: '100%'
              }}>
                <div style={{ background: '#FFF0F2', width: '36px', height: '36px', borderRadius: '8px', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#d92b3c', marginBottom: '1rem' }}>
                  {req.icon}
                </div>
                <h4 style={{ margin: '0 0 0.25rem 0', color: '#1A1A1A', fontSize: '0.9rem' }}>{req.title}</h4>
                <p style={{ margin: 0, color: '#999', fontSize: '0.8rem' }}>{req.desc}</p>
              </div>
            </Grid>
          ))}
        </Grid>
      </Grid>
      
      <ImageCarouselModal
        isOpen={carouselOpen}
        onClose={() => setCarouselOpen(false)}
        images={previewUrls}
        initialIndex={carouselIndex}
      />
    </Grid>
  );
};

export default WizardStep1Photos;
